<template>
  <div class="step-content py-4 p-0 pt-1">
    <h3 class="step-title">Monte seu evento</h3>
    <p class="step-description">
      Escolha a ocasião, personalize e selecione os presentes.
    </p>

    <div class="event-identity-card mb-4">
      <div class="event-identity-card__photo-col">
        <PhotoUploadButton
          :preview="eventPhotoPreview"
          empty-label="Foto"
          label="Adicionar foto do evento"
          :has-error="!!errors.eventPhoto"
          @click="triggerFileInput()"
          @remove="removePhoto"
        />

        <input
          type="file"
          ref="fileInput"
          accept="image/*"
          class="d-none"
          @change="handlePhotoUpload"
        />
      </div>

      <div class="event-identity-card__name-col">
        <label class="event-identity-card__label">Nome do evento</label>
        <input
          type="text"
          class="event-identity-card__input"
          :class="{ 'is-invalid': errors.eventName }"
          v-model="eventName"
          @input="emitEventDetails"
          :placeholder="eventNamePlaceholder"
          autocomplete="off"
        />
        <div v-if="errors.eventName" class="invalid-feedback d-block">
          {{ errors.eventName }}
        </div>
      </div>
    </div>

    <div class="mb-4">
      <label class="form-label">Qual é a ocasião?</label>

      <div v-if="selectedEventTypeName" class="occasion-selected-chip">
        <i class="fas fa-check-circle occasion-selected-chip__icon"></i>
        <span class="occasion-selected-chip__name">{{ selectedEventTypeName }}</span>
        <button
          type="button"
          class="occasion-selected-chip__clear"
          @click="clearEventType"
          title="Trocar ocasião"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="occasion-step-wrapper" :class="{ 'occasion-step-wrapper--collapsed': selectedEventTypeName }">
        <Step1EventType
          ref="step1Ref"
          @update:eventType="handleEventTypeUpdate"
          @update:selectedItems="handleSelectedItemsUpdate"
          @update:eventTypeName="handleEventTypeNameUpdate"
        />
      </div>
    </div>

    <ImageCropperModal
      :visible="cropperModalVisible"
      :src="eventPhotoSourceForCropper || ''"
      :aspect-ratio="1"
      title="Ajuste a foto do evento"
      subtitle="Posicione e enquadre a imagem como preferir."
      @confirm="onCropConfirm"
      @cancel="cancelCrop"
    />
  </div>
</template>

<script>
import { ref, defineExpose, computed, onMounted, onUnmounted } from 'vue';
import { toast } from 'vue3-toastify';
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import BottomModal from '@/components/BottomModal.vue';
import ImageCropperModal from '@/components/ImageCropperModal.vue';
import PhotoUploadButton from '@/components/PhotoUploadButton.vue';
import Step1EventType from '@/components/CreateEventSteps/Step1EventType.vue';

export default {
  name: 'Step2EventDetails',
  components: {
    ImageCropperModal,
    PhotoUploadButton,
    BottomModal,
    Step1EventType,
  },
  emits: ['update:eventDetails', 'update:eventType', 'update:selectedItems'],
  setup(props, { emit }) {
    const eventName = ref('');
    const eventPhoto = ref(null);
    const eventPhotoPreview = ref(null);
    const eventPhotoSourceForCropper = ref(null);
    const loadingPhoto = ref(false);
    const errors = ref({
      eventName: null,
      eventPhoto: null,
    });

    const fileInput = ref(null);
    const cropperModalVisible = ref(false);
    const step1Ref = ref(null);
    const selectedEventTypeName = ref('');

    const isCropping = computed(() => eventPhotoSourceForCropper.value !== null);

    const eventNamePlaceholders = ref([
      'Ex: Festa de Aniversário da Maria',
      'Ex: Confraternização da Empresa XYZ',
      'Ex: Casamento da Ana e do João',
      'Ex: Chá de Bebê da Bia',
      'Ex: Reunião de Amigos',
      'Ex: Lançamento do Produto X',
    ]);
    const currentPlaceholderIndex = ref(0);
    const eventNamePlaceholder = ref(eventNamePlaceholders.value[0]);
    let placeholderInterval = null;

    const emitEventDetails = () => {
      emit('update:eventDetails', {
        eventName: eventName.value,
        eventPhoto: eventPhoto.value,
      });
      validateEventName();
    };

    const handlePhotoUpload = (event) => {
      const file = event.target.files[0];
      if (file) {
        loadingPhoto.value = true;
        eventPhoto.value = null;
        eventPhotoPreview.value = null;

        const reader = new FileReader();
        reader.onload = (e) => {
          // Passa a imagem original diretamente para o cropper, sem pré-processamento.
          // A compressão acontece apenas uma vez, na saída do crop.
          eventPhotoSourceForCropper.value = e.target.result;
          loadingPhoto.value = false;
          cropperModalVisible.value = true;
        };
        reader.onerror = () => {
          toast.error('Não foi possível ler o arquivo da imagem. Tente novamente.', { position: 'top-right', autoClose: 3000 });
          eventPhotoSourceForCropper.value = null;
          loadingPhoto.value = false;
        };
        reader.readAsDataURL(file);
      } else {
        eventPhotoSourceForCropper.value = null;
        loadingPhoto.value = false;
        if (!eventPhoto.value && !eventPhotoPreview.value) {
           validateEventPhoto();
        }
      }
      event.target.value = '';
    };

    const triggerFileInput = () => {
      fileInput.value.click();
    };

    const onCropConfirm = ({ blob, dataUrl }) => {
      const fileName = 'event-photo-' + Date.now() + '.jpeg';
      eventPhoto.value = new File([blob], fileName, { type: 'image/jpeg' });
      eventPhotoPreview.value = dataUrl;
      eventPhotoSourceForCropper.value = null;
      cropperModalVisible.value = false;
      emitEventDetails();
      validateEventPhoto();
    };

    const cancelCrop = () => {
      eventPhotoSourceForCropper.value = null;
      cropperModalVisible.value = false;
      if (!eventPhoto.value) {
        validateEventPhoto();
      }
    };

    const validateEventName = () => {
      if (!eventName.value || eventName.value.trim().length < 3) {
        errors.value.eventName = 'O nome do evento deve ter no mínimo 3 caracteres.';
        return false;
      }
      errors.value.eventName = null;
      return true;
    };

    const validateEventPhoto = () => {
      if (eventPhoto.value || eventPhotoSourceForCropper.value) {
        errors.value.eventPhoto = null;
      }
      return true;
    };

    const handleEventTypeUpdate = (data) => {
      emit('update:eventType', data);
    };

    const handleSelectedItemsUpdate = (items) => {
      emit('update:selectedItems', items);
    };

    const handleEventTypeNameUpdate = (name) => {
      selectedEventTypeName.value = name;
    };

    const clearEventType = () => {
      selectedEventTypeName.value = '';
      emit('update:eventType', { eventType: null });
      emit('update:selectedItems', {});
      if (step1Ref.value && typeof step1Ref.value.resetSelection === 'function') {
        step1Ref.value.resetSelection();
      }
    };

    const validateAndProceed = (isFinalValidation = false) => {
      let step1Valid = { isValid: true };
      if (step1Ref.value && typeof step1Ref.value.validateAndProceed === 'function') {
        step1Valid = step1Ref.value.validateAndProceed(isFinalValidation);
      }

      const isNameValid = validateEventName();

      if (cropperModalVisible.value) {
        toast.info('Por favor, confirme ou cancele o corte da imagem.', {
          position: 'top-right', autoClose: 3000,
        });
        return { isValid: false, data: null };
      }

      if (!step1Valid.isValid) {
        return { isValid: false, data: null };
      }

      if (!isNameValid && isFinalValidation) {
        toast.error('Por favor, preencha o nome do evento.', {
          position: 'top-right', autoClose: 3000,
        });
        return { isValid: false, data: null };
      }

      return {
        isValid: step1Valid.isValid && isNameValid,
        data: {
          eventName: eventName.value,
          eventPhoto: eventPhoto.value, // Agora eventPhoto.value será um File object
        }
      };
    };

    const startPlaceholderRotation = () => {
      placeholderInterval = setInterval(() => {
        currentPlaceholderIndex.value = (currentPlaceholderIndex.value + 1) % eventNamePlaceholders.value.length;
        eventNamePlaceholder.value = eventNamePlaceholders.value[currentPlaceholderIndex.value];
      }, 4000);
    };

    const stopPlaceholderRotation = () => {
      if (placeholderInterval) {
        clearInterval(placeholderInterval);
        placeholderInterval = null;
      }
    };

    onMounted(() => {
      startPlaceholderRotation();
    });

    onUnmounted(() => {
      stopPlaceholderRotation();
    });

    const removePhoto = () => {
      eventPhoto.value = null;
      eventPhotoPreview.value = null;
      emit('update:eventDetails', { eventName: eventName.value, eventPhoto: null });
    };

    const handleDrop = (e) => {
      const file = e.dataTransfer.files[0];
      if (file) handlePhotoUpload({ target: { files: [file] } });
    };

    defineExpose({
      validateAndProceed,
    });

    return {
      eventName,
      eventPhoto,
      eventPhotoPreview,
      eventPhotoSourceForCropper,
      loadingPhoto,
      errors,
      fileInput,
      isCropping,
      eventNamePlaceholder,
      cropperModalVisible,
      handlePhotoUpload,
      emitEventDetails,
      triggerFileInput,
      onCropConfirm,
      cancelCrop,
      validateAndProceed,
      removePhoto,
      handleDrop,
      selectedEventTypeName,
      step1Ref,
      handleEventTypeUpdate,
      handleSelectedItemsUpdate,
      handleEventTypeNameUpdate,
      clearEventType,
    };
  },
};
</script>

<style scoped>
.step-content { padding: 1rem 0; }
.step-content {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: clip;
  box-sizing: border-box;
}
.step-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #32355d;
  margin-bottom: 0.25rem;
}

.step-description {
  font-size: 0.95rem;
  color: #6c757d;
  margin-bottom: 1.5rem;
  line-height: 1.5;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  font-weight: 600;
  color: #32355d;
  margin-bottom: 0.5rem;
  display: block;
}

.form-control {
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 0.625rem;
  padding: 0.75rem 1rem;
  font-size: 0.95rem;
  color: #32355d;
  width: 100%;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control::placeholder {
  color: #b0b0b0;
  font-size: 0.9rem;
}

.form-control:focus {
  background-color: #ffffff;
  border-color: #ff50a3;
  box-shadow: 0 0 0 0.2rem rgba(255, 80, 163, 0.18);
  outline: none;
}

.form-control.is-invalid {
  border-color: #dc3545;
  padding-right: calc(1.5em + 0.75rem);
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23dc3545'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right calc(0.375em + 0.1875rem) center;
  background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
}

.invalid-feedback {
  color: #dc3545;
  font-size: 0.875em;
  margin-top: 0.25rem;
}

.photo-upload-card {
  width: 100%;
  height: 200px;
  border: 2px dashed #ced4da;
  border-radius: 0.75rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  background-color: #f8f9fa;
  transition: border-color 0.2s ease, background-color 0.2s ease;
  position: relative;
  overflow: hidden;
  margin-bottom: 1rem;
}

.photo-upload-card:hover {
  border-color: #ff50a3;
  background-color: #e9ecef;
}

.photo-upload-card.is-invalid {
  border-color: #dc3545;
}

.upload-icon {
  font-size: 3rem;
  color: #adb5bd;
  margin-bottom: 0.5rem;
}

.upload-text {
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.25rem;
}

.upload-subtext {
  font-size: 0.85rem;
}

.photo-preview-image {
  max-width: 100%;
  max-height: 100%;
  display: block;
  object-fit: contain;
  border-radius: 0.75rem;
  margin: auto;
}

.change-photo-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 0.75rem;
  font-weight: bold;
}

.photo-upload-card:hover .change-photo-overlay {
  opacity: 1;
}

.change-photo-overlay .fas {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 10;
  border-radius: 0.75rem;
}

.loading-spinner {
  font-size: 3rem;
  color: #ff50a3;
  margin-bottom: 1rem;
}

.loading-text {
  font-size: 1.1rem;
  font-weight: 600;
  color: #32355d;
}

/* ── Upload de foto ────────────────────────────────────────────── */
.photo-upload-area {
  border: 2px dashed #e0e0e0;
  border-radius: 1rem;
  padding: 2rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background: #fafafa;
}

.photo-upload-area:hover {
  border-color: #ff50a3;
  background: #fff0f6;
}

.photo-upload-area__icon {
  font-size: 2rem;
  color: #c0c0c0;
  margin-bottom: 0.5rem;
  display: block;
}

.photo-upload-area:hover .photo-upload-area__icon {
  color: #ff50a3;
}

.photo-upload-area__text {
  font-size: 0.9rem;
  font-weight: 600;
  color: #6c757d;
  margin: 0 0 4px;
}

.photo-upload-area__hint {
  font-size: 0.75rem;
  color: #b0b0b0;
  margin: 0;
}

/* Preview da foto */
.photo-preview-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.photo-preview {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  overflow: hidden;
  border: 3px solid #ff50a3;
  box-shadow: 0 4px 14px rgba(255, 80, 163, 0.2);
}

.photo-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.btn-photo-remove {
  background: none;
  border: 1px solid #f5c6cb;
  color: #c0392b;
  font-size: 0.8rem;
  border-radius: 99px;
  padding: 4px 14px;
  cursor: pointer;
  transition: all 0.18s;
}

.btn-photo-remove:hover {
  background: #fdf3f2;
  border-color: #e74c3c;
}

/* ── Card de identidade do evento ────────────────────────── */
.event-identity-card {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 1.25rem;
  padding: 1rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.event-identity-card__photo-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.event-identity-card__name-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-top: 4px;
}

.event-identity-card__label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #9e9e9e;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 5px;
}

.event-identity-card__input {
  background: #fafafa;
  border: 1.5px solid #e8e8e8;
  border-radius: 0.75rem;
  padding: 0.6rem 0.875rem;
  font-size: 0.95rem;
  font-weight: 600;
  color: #32355d;
  width: 100%;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.event-identity-card__input::placeholder {
  color: #c0c0c0;
  font-weight: 400;
}

.event-identity-card__input:focus {
  outline: none;
  border-color: #ff50a3;
  background: #fff;
  box-shadow: 0 0 0 0.2rem rgba(255, 80, 163, 0.14);
}

.event-identity-card__input.is-invalid {
  border-color: #dc3545;
}

/* ── Chip de ocasião selecionada ─────────────────────────── */
.occasion-selected-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: #fff0f6;
  border: 1.5px solid #ff50a3;
  border-radius: 99px;
  font-size: 0.88rem;
  font-weight: 700;
  color: #ff50a3;
  max-width: 100%;
}

.occasion-selected-chip__icon {
  font-size: 0.85rem;
}

.occasion-selected-chip__name {
  flex: 1;
  min-width: 0;
  line-height: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.occasion-selected-chip__clear {
  background: none;
  border: none;
  color: #ff50a3;
  cursor: pointer;
  padding: 0 0 0 4px;
  font-size: 0.8rem;
  line-height: 1;
  opacity: 0.7;
  transition: opacity 0.15s;
  display: flex;
  align-items: center;
}

.occasion-selected-chip__clear:hover {
  opacity: 1;
}

.occasion-step-wrapper :deep(.step-content) {
  padding: 0;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: hidden;
}

.occasion-step-wrapper {
  width: 100%;
  min-width: 0;
  overflow-x: hidden;
}

.occasion-step-wrapper :deep(.event-type-chip) {
  max-width: 100%;
  min-width: 0;
}

.occasion-step-wrapper :deep(.event-type-chip__name) {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.occasion-step-wrapper :deep(.event-item-card__name) {
  overflow-wrap: anywhere;
  word-break: break-word;
}

.occasion-step-wrapper :deep(.step-title),
.occasion-step-wrapper :deep(.step-description),
.occasion-step-wrapper :deep(.step-content > .mb-4 > .form-label) {
  display: none;
}

.occasion-step-wrapper.occasion-step-wrapper--collapsed :deep(.step-content > .mb-4) {
  display: none;
}

@media (max-width: 767.98px) {
  .event-identity-card {
    padding: 0.875rem;
    gap: 0.75rem;
  }

  .event-identity-card__input {
    font-size: 16px;
  }

  .occasion-step-wrapper :deep(input),
  .occasion-step-wrapper :deep(textarea),
  .occasion-step-wrapper :deep(select),
  .occasion-step-wrapper :deep(.form-control),
  .occasion-step-wrapper :deep(.form-select) {
    font-size: 16px !important;
  }

  .occasion-step-wrapper :deep(.step-content > .d-flex.justify-content-between.align-items-center.mb-3) {
    flex-wrap: wrap;
    gap: 8px;
  }

  .occasion-step-wrapper :deep(.step-content > .d-flex.justify-content-between.align-items-center.mb-3 > button) {
    width: 100%;
  }

  .occasion-step-wrapper :deep(.event-items-grid) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>